import React, { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../shared/utility";
import ProcessingHistoryDetails from "../../components/forms/inProcessMonitoring/ProcessingHistoryDetails";
import { startOfDay, endOfDay } from "date-fns";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import FilterInput from "../../components/filterDropdown/FilterInput";

export interface CurrentStatusHistory {
  id: number;
  production_id: number;
  in_process_status_id: number;
  in_process_status_name: string;
  user_id: number;
  user_name: string;
  elapsed_seconds: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface NextStatusAssigned {
  id: number;
  process_status_id: number;
  product_id: number;
  process_status_order: number;
  product_status_name: string | null;
  created_at: string;
  updated_at: string;
  status_id: number;
  status_name: string;
  status_colour: string | null;
}

export interface InProcessMonitoringHistory {
  id: number;
  plu: string;
  product_id: number;
  production_id: number;
  in_process_status_id: number;
  in_process_status_name: string;
  user_id: number;
  user_name: string;
  elapsed_seconds: number;
  next_process_status_id: number | null;
  next_process_status_name: string | null;
  description_en: string;
  required_total_raw_material: string | null;
  production_date: string;
  current_status_history: CurrentStatusHistory;
  next_status_assigned: NextStatusAssigned | null;
}

export interface PaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface InProcessMonitoringHistoriesResponse {
  current_page: number;
  data: InProcessMonitoringHistory[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: PaginationLink[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export function InProcessMonitoringViewModel(
  logout: () => void,
  addSnackbar: (snackbar: any) => void
) {
  const [loading, setLoading] = useState(false);
  const [histories, setHistories] =
    useState<InProcessMonitoringHistoriesResponse>({
      current_page: 1,
      data: [],
      first_page_url: "",
      from: 0,
      last_page: 1,
      last_page_url: "",
      links: [],
      next_page_url: null,
      path: "",
      per_page: 20,
      prev_page_url: null,
      to: 0,
      total: 0,
    });
  const [productsForDropdown, setProductsForDropdown] = useState<
    { value: number; label: string }[]
  >([]);

  const [emptyModal, setEmptyModelOpen] = useState<{
    open: boolean;
    childComponent: any;
  }>({
    open: false,
    childComponent: null,
  });
  const [slideOver, setSlideOver] = useState<{
    open: boolean;
    childComponent: any;
  }>({
    childComponent: null,
    open: false,
  });
  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [showFilters, setShowFilters] = useState(false);

  const [showDates, setShowDates] = useState(false);
  const [recipes, setRecipes] = useState<{ value: number; label: string }[]>(
    []
  );
  const [supervisorOptions, setSupervisorOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [employeeOptions, setEmployeeOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [filters, setFilters] = useState({
    product: { value: -1, label: "All Products" },
    supervisor: { value: -1, label: "All Employees" },
    employee1: { value: -1, label: "All Employees" },
    employee2: { value: -1, label: "All Employees" },
    certification: { value: -1, label: "All Certifications" },
    recipe: { value: -1, label: "All Recipes" },
    grandTotal: null,
    // default range: today from 12:00 AM to 11:59:59.999 PM
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
    product_lot: null,
  });
  const [reportFilters, setReportFilters] = useState({
    type: { value: -1, label: "Select report" },
  });
  const [certifications, setCertifications] = useState<
    { value: number; label: string }[]
  >([]);

  const resetFilters = () => {
    const filterReset = {
      product: { value: -1, label: "All Products" },
      supervisor: { value: -1, label: "All Employees" },
      employee1: { value: -1, label: "All Employees" },
      employee2: { value: -1, label: "All Employees" },
      certification: { value: -1, label: "All Certifications" },
      recipe: { value: -1, label: "All Recipes" },
      grandTotal: null,
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    };
    setFilters(filterReset as any);

    setShowFilters(false);
    getInProcessMonitoringHistories(null, 1, filterReset);
  };

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getInProcessMonitoringHistories = async function (
    search: any,
    page: any,
    filterArg: any
  ) {
    setLoading(true);
    const payload = {
      page: page,
      filters: { ...filterArg, search: search },
    };
    const callResult: any = await makePostApiCall(
      "inProcessMonitoringHistories",
      payload
    );

    if (callResult.success) {
      setHistories(callResult.data as InProcessMonitoringHistoriesResponse);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      showErrorModal(callResult.data.message || "Could not load histories");
    }
    setLoading(false);
  };

  const updateInProcessMonitoringHistory = async function (payload: any) {
    const callResult: any = await makePostApiCall(
      `inProcessHistoryOnNextStatus`,
      payload
    );

    if (callResult.success) {
      resetFilters();
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update history",
            SnackbarType.Error
          )
        );
      }
      showErrorModal(callResult.data.message || "Could not update history");
    }
  };

  const previousInProcessMonitoringHistory = async function (payload: any) {
    const callResult: any = await makePostApiCall(
      `inProcessHistoryOnPreviousStatus`,
      payload
    );

    if (callResult.success) {
      resetFilters();
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      if (addSnackbar) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not update history",
            SnackbarType.Error
          )
        );
      }
      showErrorModal(callResult.data.message || "Could not update history");
    }
  };

  const fetchInProcessMonitoringHistoryByProductionId = async function (
    historyId: number
  ) {
    const callResult: any = await makeGetApiCall(
      `inProcessMonitoringHistoryByProductionId/${historyId}`
    );

    if (callResult.success) {
      setSlideOver({
        open: true,
        childComponent: (
          <ProcessingHistoryDetails
            inProcessMonitoringHistory={callResult.data}
            setOpen={setSlideOver}
          />
        ),
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
        return;
      }
      showErrorModal(callResult.data.message || "Could not fetch history");
    }
  };

  const fetchProductsForDropdown = async function () {
    let callResult: any = await makePostApiCall(`fetchProductsForDropdown`);

    if (callResult.success) {
      setProductsForDropdown([
        { value: -1, label: "All Products" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCertifiedByOptionsForDropdown = async function () {
    let callResult: any = await makePostApiCall(
      `certifiedByOptionsForDropdown`
    );

    if (callResult.success) {
      setCertifications([
        { value: -1, label: "All Certifications" },
        ...callResult.data,
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchRecipesForDropdown = async function () {
    let callResult: any = await makePostApiCall(`recipesForDropdown`);

    if (callResult.success) {
      setRecipes([{ value: -1, label: "All Recipes" }, ...callResult.data]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getUsersForDropdown = async function (type: any, department: any) {
    let callResult: any = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      if (type === 1) {
        setEmployeeOptions(callResult.data);
      } else {
        setSupervisorOptions(callResult.data);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Product"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          product: value,
        })
      }
      options={productsForDropdown}
      resetFilter={() =>
        setFilters({
          ...filters,
          product: { value: -1, label: "All Products" },
        })
      }
      valueName="product"
      value={filters.product}
    />,
    <FilterInput
      header="Search by product lot"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          product_lot: value,
        })
      }
      resetFilter={() =>
        setFilters({
          ...filters,
          product_lot: null,
        })
      }
      valueName="product_lot"
      value={filters.product_lot}
    />,
    <FilterSingleSelectDropdown
      header="Certification"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          certification: value,
        })
      }
      options={certifications as any}
      resetFilter={() =>
        setFilters({
          ...filters,
          certification: { value: -1, label: "All Certifications" },
        })
      }
      valueName="certification"
      value={filters.certification}
    />,
    <FilterSingleSelectDropdown
      header="Recipe"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          recipe: value,
        })
      }
      options={recipes}
      resetFilter={() =>
        setFilters({
          ...filters,
          recipe: { value: -1, label: "All Recipes" },
        })
      }
      valueName="recipe"
      value={filters.recipe}
    />,
    <FilterSingleSelectDropdown
      header="Supervisor"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          supervisor: value,
        })
      }
      options={supervisorOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          supervisor: { value: -1, label: "All Employees" },
        })
      }
      valueName="supervisor"
      value={filters.supervisor}
    />,
    <FilterSingleSelectDropdown
      header="Employee 1"
      onChange={(value: any) =>
        setFilters({
          ...filters,
          employee1: value,
        })
      }
      options={employeeOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          employee1: { value: -1, label: "All Employees" },
        })
      }
      valueName="employee1"
      value={filters.employee1}
    />,
  ];

  return {
    getInProcessMonitoringHistories,
    histories,
    loading,
    setHistories,
    setLoading,
    updateInProcessMonitoringHistory,
    fetchInProcessMonitoringHistoryByProductionId,
    emptyModal,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    filters,
    setFilters,
    showDates,
    setShowDates,
    analytics,
    showFilters,
    setShowFilters,
    filterComponents,
    resetFilters,
    getUsersForDropdown,
    fetchProductsForDropdown,
    productsForDropdown,
    getCertifiedByOptionsForDropdown,
    certifications,
    fetchRecipesForDropdown,
    recipes,
    supervisorOptions,
    employeeOptions,
    previousInProcessMonitoringHistory,
  };
}

export default InProcessMonitoringViewModel;
